import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axiosInstance from '../../lib/axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Tag, Cpu, Loader2, Plus, Trash2 } from 'lucide-react';
import { mtokFromOpenRouter } from '../lib/dashboardFormat';

/**
 * #13 — unified "Modelos y precios" admin section.
 *
 * Fuses the two former panels into one: the per-task model assignment for the
 * BASE service (non-BYOK) and the model_pricing catalog (costs + active + CRUD).
 * The task dropdowns list ONLY catalog model_ids (no free text) so a base-service
 * task can never point at an unpriced/unknown model. BYOK is unaffected.
 *
 * Shared by the root and tiptap AdminPanels (self-contained: reads /admin/pricing,
 * /admin/settings and /ai/models; writes /admin/pricing and /admin/settings).
 */
const SELECT_CLS =
  'w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 px-3 text-sm';
const OPT_CLS = 'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100';

const TASK_FIELDS = [
  { key: 'default_chat_model', label: 'Chat' },
  { key: 'default_image_model', label: 'Imagen' },
  { key: 'default_describe_model', label: 'Descripción (visión)' },
  { key: 'default_comment_model', label: 'Comentarios' },
];

const emptyPricing = {
  model_id: '', input_cost_per_mtok: 0, output_cost_per_mtok: 0,
  image_cost: 0, request_cost: 0, active: true,
};

const emptyTasks = {
  default_chat_model: '', default_image_model: '',
  default_describe_model: '', default_comment_model: '',
  default_chat_model_fallback: '', default_image_model_fallback: '',
  default_describe_model_fallback: '', default_comment_model_fallback: '',
};

const ModelsPricingCard = () => {
  const [pricing, setPricing] = useState([]);
  const [tasks, setTasks] = useState(emptyTasks);
  const [savingTasks, setSavingTasks] = useState(false);

  // Cargo mínimo por llamada (créditos) — facturación. El backend lo expone en
  // GET/PUT /admin/settings (float ≥ 0; 0 desactiva el mínimo). Se carga junto
  // con los modelos por tarea (mismo GET de /admin/settings) y se persiste con
  // su propio PUT parcial, imitando el bloque "Configuración de créditos" de
  // AdminDashboard (settings state + saveConfig → PUT /admin/settings).
  const [minCharge, setMinCharge] = useState('');
  const [savingMinCharge, setSavingMinCharge] = useState(false);

  // T-208: modelos para chat (elección del usuario). Lista de {model_id,
  // description} que el admin cura; el usuario los ve en el selector del chat.
  // Se carga del mismo GET /admin/settings (campo chat_model_choices) y se
  // persiste con un PUT parcial, igual que saveMinCharge.
  const [chatModelChoices, setChatModelChoices] = useState([]);
  const [savingChatChoices, setSavingChatChoices] = useState(false);

  // OpenRouter catalog (for the searchable picker inside the pricing modal).
  const [models, setModels] = useState([]);

  const [pricingForm, setPricingForm] = useState(null); // null = closed
  const [pricingBusy, setPricingBusy] = useState(false);

  const loadPricing = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/admin/pricing');
      setPricing(res.data.pricing || []);
    } catch (e) { /* interceptor handles 403 */ }
  }, []);

  const loadTasks = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/admin/settings');
      setTasks(Object.fromEntries(
        Object.keys(emptyTasks).map((k) => [k, res.data[k] || ''])
      ));
      // Cargo mínimo por llamada: mismo GET de /admin/settings que los modelos
      // por tarea (una sola petición alimenta ambos bloques).
      setMinCharge(res.data.min_charge_credits ?? '');
      // T-208: modelos para chat (elección del usuario).
      setChatModelChoices(Array.isArray(res.data.chat_model_choices) ? res.data.chat_model_choices : []);
    } catch (e) { /* ignore */ }
  }, []);

  const loadModels = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/ai/models');
      setModels(res.data.models || []);
    } catch (e) { /* picker just shows an empty list */ }
  }, []);

  useEffect(() => { loadPricing(); loadTasks(); loadModels(); },
    [loadPricing, loadTasks, loadModels]);

  // Active catalog ids feed every task dropdown (base service = catalog only).
  const activeIds = useMemo(
    () => pricing.filter((p) => p.active).map((p) => p.model_id),
    [pricing]
  );

  // Single combobox: the model_id field doubles as the search query.
  const filteredModels = useMemo(() => {
    const q = (pricingForm?.model_id || '').trim().toLowerCase();
    const list = q
      ? models.filter((m) => (m.id || '').toLowerCase().includes(q) || (m.name || '').toLowerCase().includes(q))
      : models;
    return list.slice(0, 50);
  }, [models, pricingForm?.model_id]);

  const saveTasks = async () => {
    setSavingTasks(true);
    try {
      await axiosInstance.put('/admin/settings', tasks);
      toast.success('Modelos por tarea guardados');
      loadTasks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar modelos');
    } finally {
      setSavingTasks(false);
    }
  };

  // Guarda SOLO el cargo mínimo (PUT parcial a /admin/settings con un subset de
  // claves), mismo mecanismo que saveConfig de AdminDashboard: PUT + toast +
  // reload. El backend trata /admin/settings como actualización parcial.
  const saveMinCharge = async () => {
    setSavingMinCharge(true);
    try {
      await axiosInstance.put('/admin/settings', {
        min_charge_credits: Number(minCharge) || 0,
      });
      toast.success('Cargo mínimo guardado');
      loadTasks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar el cargo mínimo');
    } finally {
      setSavingMinCharge(false);
    }
  };

  // T-208: agregar / quitar / actualizar un choice de chat-model.
  const addChatChoice = () => {
    setChatModelChoices((prev) => [...prev, { model_id: '', description: '' }]);
  };
  const removeChatChoice = (idx) => {
    setChatModelChoices((prev) => prev.filter((_, i) => i !== idx));
  };
  const updateChatChoice = (idx, field, value) => {
    setChatModelChoices((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  };

  // Guarda SOLO los chat_model_choices (PUT parcial a /admin/settings), mismo
  // mecanismo que saveMinCharge: PUT + toast + reload.
  const saveChatChoices = async () => {
    setSavingChatChoices(true);
    try {
      // Filtrar entradas vacías y truncar descripciones a 255 caracteres.
      const cleaned = chatModelChoices
        .filter((c) => c.model_id && c.model_id.trim())
        .map((c) => ({
          model_id: c.model_id.trim(),
          description: (c.description || '').slice(0, 255),
        }));
      await axiosInstance.put('/admin/settings', { chat_model_choices: cleaned });
      toast.success('Modelos para chat guardados');
      setChatModelChoices(cleaned);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar modelos para chat');
    } finally {
      setSavingChatChoices(false);
    }
  };

  const pickModel = (m) => {
    setPricingForm((f) => {
      const next = { ...f, model_id: m.id };
      const mtok = mtokFromOpenRouter(m.pricing);
      if (mtok) {
        next.input_cost_per_mtok = mtok.input_cost_per_mtok;
        next.output_cost_per_mtok = mtok.output_cost_per_mtok;
      }
      return next;
    });
  };

  const savePricing = async () => {
    if (!pricingForm.model_id?.trim()) { toast.error('model_id requerido'); return; }
    setPricingBusy(true);
    try {
      await axiosInstance.put('/admin/pricing', {
        model_id: pricingForm.model_id.trim(),
        input_cost_per_mtok: Number(pricingForm.input_cost_per_mtok) || 0,
        output_cost_per_mtok: Number(pricingForm.output_cost_per_mtok) || 0,
        image_cost: Number(pricingForm.image_cost) || 0,
        request_cost: Number(pricingForm.request_cost) || 0,
        active: !!pricingForm.active,
      });
      toast.success('Precio del modelo guardado');
      setPricingForm(null);
      loadPricing();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar precio');
    } finally {
      setPricingBusy(false);
    }
  };

  const deletePricing = async (modelId) => {
    if (!window.confirm(`¿Eliminar "${modelId}" del catálogo de precios?`)) return;
    try {
      await axiosInstance.delete(`/admin/pricing/${encodeURIComponent(modelId)}`);
      toast.success('Modelo eliminado del catálogo');
      loadPricing();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar el modelo');
    }
  };

  // Toggle a model's active flag in place (reuses the upsert; preserves costs).
  const toggleActive = async (p) => {
    try {
      await axiosInstance.put('/admin/pricing', {
        model_id: p.model_id,
        input_cost_per_mtok: p.input_cost_per_mtok,
        output_cost_per_mtok: p.output_cost_per_mtok,
        image_cost: p.image_cost,
        request_cost: p.request_cost,
        active: !p.active,
      });
      toast.success(p.active ? 'Modelo desactivado' : 'Modelo activado');
      loadPricing();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cambiar el estado');
    }
  };

  // Options for a task dropdown: active catalog ids, plus the current value even
  // if it is inactive/absent so a legacy setting still renders as selected.
  const optionsFor = (value) =>
    (value && !activeIds.includes(value)) ? [value, ...activeIds] : activeIds;

  return (
    <details className="glass rounded-xl p-6" open data-testid="models-pricing-card">
      <summary className="cursor-pointer select-none text-sm font-medium text-slate-700 dark:text-slate-200">
        <span className="inline-flex items-center gap-2 align-middle">
          <Cpu className="w-4 h-4 text-purple-600" /> Modelos y precios
        </span>
      </summary>
      <div className="mt-4 space-y-5">
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Asignación de modelo por tarea del <strong>servicio base</strong> (usuarios
          sin llave BYOK). Solo puedes elegir modelos del catálogo de abajo. Con BYOK
          el usuario usa su propio modelo.
        </p>

      {/* Per-task assignment (base service, non-BYOK) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {TASK_FIELDS.map((f) => {
          const fbKey = `${f.key}_fallback`;
          return (
            <div key={f.key} className="space-y-1">
              <Label>{f.label}</Label>
              <select
                data-testid={`task-${f.key}`}
                value={tasks[f.key] || ''}
                onChange={(e) => setTasks((t) => ({ ...t, [f.key]: e.target.value }))}
                className={SELECT_CLS}
              >
                <option value="" className={OPT_CLS}>— usar predeterminado —</option>
                {optionsFor(tasks[f.key]).map((id) => (
                  <option key={id} value={id} className={OPT_CLS}>{id}</option>
                ))}
              </select>
              {/* T-208 (ítem 8): botón que intercambia el modelo default y el
                  fallback en el estado local. El guardado sigue siendo el botón
                  "Guardar modelos por tarea" existente — swap client-side, sin
                  endpoint nuevo. */}
              <button
                type="button"
                data-testid={`swap-${f.key}`}
                title="Intercambia el modelo default y el fallback (p.ej. para activar un modelo free)"
                onClick={() => setTasks((t) => ({
                  ...t,
                  [f.key]: t[fbKey],
                  [fbKey]: t[f.key],
                }))}
                className="block ml-auto text-xs text-sky-600 hover:text-sky-700 dark:text-sky-400 dark:hover:text-sky-300 py-0.5"
              >
                ⇄ Alternar
              </button>
              <select
                data-testid={`task-${fbKey}`}
                title="Fallback: se usa si el modelo principal se desactiva o elimina del catálogo"
                value={tasks[fbKey] || ''}
                onChange={(e) => setTasks((t) => ({ ...t, [fbKey]: e.target.value }))}
                className={`${SELECT_CLS} text-slate-500 dark:text-slate-400`}
              >
                <option value="" className={OPT_CLS}>— sin fallback —</option>
                {optionsFor(tasks[fbKey]).map((id) => (
                  <option key={id} value={id} className={OPT_CLS}>Fallback: {id}</option>
                ))}
              </select>
            </div>
          );
        })}
      </div>
      <p className="text-xs rounded-md bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 px-3 py-2">
        ⚠️ Para que el chat pueda <strong>escribir en las hojas</strong>, el modelo de{' '}
        <strong>Chat</strong> debe soportar <em>tool-calling</em> (function calling). Con BYOK
        se usa el modelo del usuario; sin BYOK, este modelo por tarea. Si el modelo elegido no
        soporta herramientas, el chat responde pero no edita.
      </p>
      <Button data-testid="save-task-models" onClick={saveTasks} disabled={savingTasks}
        className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700">
        {savingTasks ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Guardar modelos por tarea
      </Button>

      {/* Cargo mínimo por llamada — facturación (GET/PUT /admin/settings).
          Mismo patrón que el bloque "Configuración de créditos" de AdminDashboard:
          Label + Input(numérico, step) + ayuda + botón Guardar con spinner. */}
      <div className="space-y-2">
        <div className="space-y-1 sm:max-w-xs">
          <Label>Cargo mínimo por llamada (créditos)</Label>
          <Input type="number" step="0.01" min="0" value={minCharge}
            onChange={(e) => setMinCharge(e.target.value)}
            data-testid="min-charge-input" />
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Llamadas que cuesten menos se cobran a este mínimo. 0 lo desactiva.
        </p>
        <Button data-testid="save-min-charge" onClick={saveMinCharge} disabled={savingMinCharge}
          className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700">
          {savingMinCharge ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Guardar cargo mínimo
        </Button>
      </div>

      {/* T-208 (ítem 6): Modelos para chat (elección del usuario). El admin cura
          la lista de modelos que el usuario puede elegir en el chat. Cada choice
          tiene un select (opciones = model_ids del catálogo de pricing, igual
          que los selects por tarea) + input de descripción (maxLength 255 con
          contador) + botón quitar. Botón "Agregar modelo" + Guardar → PUT
          parcial {chat_model_choices}. */}
      <div className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="text-sm font-medium flex items-center gap-2">
            <Cpu className="w-4 h-4 text-purple-600" /> Modelos para chat (elección del usuario)
          </span>
          <Button size="sm" onClick={addChatChoice} data-testid="chat-choice-add">
            <Plus className="w-4 h-4 mr-1" /> Agregar modelo
          </Button>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Modelos que el usuario puede elegir en el chat. Solo aparecen los modelos del catálogo marcados como activos.
        </p>
        {chatModelChoices.length === 0 && (
          <p className="text-xs text-slate-500 dark:text-slate-400 py-2">
            No hay modelos configurados para chat. Agrega uno para que los usuarios puedan elegirlo.
          </p>
        )}
        {chatModelChoices.map((choice, idx) => (
          <div key={idx} className="flex items-start gap-2">
            <div className="flex-1 space-y-1">
              <select
                data-testid={`chat-choice-model-${idx}`}
                value={choice.model_id || ''}
                onChange={(e) => updateChatChoice(idx, 'model_id', e.target.value)}
                className={SELECT_CLS}
              >
                <option value="" className={OPT_CLS}>— selecciona un modelo —</option>
                {optionsFor(choice.model_id).map((id) => (
                  <option key={id} value={id} className={OPT_CLS}>{id}</option>
                ))}
              </select>
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  maxLength={255}
                  value={choice.description || ''}
                  onChange={(e) => updateChatChoice(idx, 'description', e.target.value)}
                  placeholder="Descripción (máx. 255 caracteres)"
                  className="flex-1"
                  data-testid={`chat-choice-desc-${idx}`}
                />
                <span className="text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap">
                  {(choice.description || '').length}/255
                </span>
              </div>
            </div>
            <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 mt-0.5 shrink-0"
              title="Quitar modelo"
              aria-label={`Quitar modelo ${choice.model_id || idx}`}
              onClick={() => removeChatChoice(idx)}
              data-testid={`chat-choice-remove-${idx}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
        {chatModelChoices.length > 0 && (
          <Button data-testid="chat-choices-save" onClick={saveChatChoices} disabled={savingChatChoices}
            className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700">
            {savingChatChoices ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Guardar modelos para chat
          </Button>
        )}
      </div>

      {/* Catalog table */}
      <div className="border-t border-slate-200 dark:border-slate-700 pt-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="text-sm font-medium flex items-center gap-2">
            <Tag className="w-4 h-4 text-purple-600" /> Catálogo de modelos (precios)
          </span>
          <Button size="sm" onClick={() => setPricingForm({ ...emptyPricing })}>
            <Plus className="w-4 h-4 mr-1" /> Agregar
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                <th className="py-2 pr-2">Modelo</th><th className="py-2 px-2 text-right">In $/Mtok</th>
                <th className="py-2 px-2 text-right">Out $/Mtok</th><th className="py-2 px-2 text-right">Imagen</th>
                <th className="py-2 px-2 text-right">Request</th><th className="py-2 px-2">Activo</th><th className="py-2 pl-2"></th>
              </tr>
            </thead>
            <tbody>
              {pricing.map((p) => (
                <tr key={p.model_id} className="border-b border-slate-100 dark:border-slate-800">
                  <td className="py-2 pr-2 font-mono text-xs">{p.model_id}</td>
                  <td className="py-2 px-2 text-right">{p.input_cost_per_mtok}</td>
                  <td className="py-2 px-2 text-right">{p.output_cost_per_mtok}</td>
                  <td className="py-2 px-2 text-right">{p.image_cost}</td>
                  <td className="py-2 px-2 text-right">{p.request_cost}</td>
                  <td className="py-2 px-2">{p.active ? 'Sí' : 'No'}</td>
                  <td className="py-2 pl-2 text-right whitespace-nowrap">
                    <Button variant="outline" size="sm" onClick={() => setPricingForm({ ...emptyPricing, ...p })}>Editar</Button>
                    <Button variant="outline" size="sm" className="ml-1"
                      title={p.active ? 'Desactivar (deja de usarse, se conserva)' : 'Activar'}
                      onClick={() => toggleActive(p)}>
                      {p.active ? 'Desactivar' : 'Activar'}
                    </Button>
                    <Button variant="outline" size="sm" className="ml-1 text-red-600 hover:text-red-700"
                      aria-label={`Eliminar ${p.model_id}`} title="Eliminar del catálogo"
                      onClick={() => deletePricing(p.model_id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
              {pricing.length === 0 && (
                <tr><td colSpan={7} className="py-3 text-slate-500 dark:text-slate-400">Sin precios configurados.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pricing modal */}
      <Dialog open={!!pricingForm} onOpenChange={(o) => { if (!o) setPricingForm(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Precio de modelo</DialogTitle>
            <DialogDescription>Override del costo de OpenRouter para este modelo.</DialogDescription>
          </DialogHeader>
          {pricingForm && (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Modelo (OpenRouter)</Label>
                <Input value={pricingForm.model_id}
                  onChange={(e) => setPricingForm((f) => ({ ...f, model_id: e.target.value }))}
                  placeholder="Escribe para buscar o pega un id (openai/gpt-4o-mini)"
                  className="font-mono" data-testid="pricing-model-id" />
                <div className="max-h-40 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
                  {filteredModels.length === 0 && (
                    <div className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">
                      {models.length === 0 ? 'Sin catálogo de modelos disponible.' : 'Ningún modelo coincide.'}
                    </div>
                  )}
                  {filteredModels.map((m) => (
                    <button type="button" key={m.id} onClick={() => pickModel(m)}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-100 dark:hover:bg-slate-800 ${m.id === pricingForm.model_id ? 'bg-purple-50 dark:bg-purple-900/30' : ''}`}>
                      <span className="font-mono">{m.id}</span>
                      {m.name && m.name !== m.id && <span className="text-slate-500 dark:text-slate-400"> · {m.name}</span>}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Al elegir un modelo se prellenan los costos (por millón de tokens) desde OpenRouter; puedes ajustarlos.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="space-y-1"><Label>Input $/Mtok</Label>
                  <Input type="number" step="0.01" value={pricingForm.input_cost_per_mtok}
                    onChange={(e) => setPricingForm((f) => ({ ...f, input_cost_per_mtok: e.target.value }))} /></div>
                <div className="space-y-1"><Label>Output $/Mtok</Label>
                  <Input type="number" step="0.01" value={pricingForm.output_cost_per_mtok}
                    onChange={(e) => setPricingForm((f) => ({ ...f, output_cost_per_mtok: e.target.value }))} /></div>
                <div className="space-y-1"><Label>Costo imagen</Label>
                  <Input type="number" step="0.001" value={pricingForm.image_cost}
                    onChange={(e) => setPricingForm((f) => ({ ...f, image_cost: e.target.value }))} /></div>
                <div className="space-y-1"><Label>Costo request</Label>
                  <Input type="number" step="0.001" value={pricingForm.request_cost}
                    onChange={(e) => setPricingForm((f) => ({ ...f, request_cost: e.target.value }))} /></div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!pricingForm.active}
                  onChange={(e) => setPricingForm((f) => ({ ...f, active: e.target.checked }))} />
                Activo
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPricingForm(null)}>Cancelar</Button>
            <Button onClick={savePricing} disabled={pricingBusy}>
              {pricingBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </details>
  );
};

export default ModelsPricingCard;
